import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, KeyRound, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const MIN_LEN = 8;

const PasswordField = ({ id, label, value, onChange, show, setShow, testId, autoComplete }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <div className="relative">
      <Input
        id={id}
        type={show ? "text" : "password"}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="h-11 pr-11 hh-focus"
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[color:var(--hh-ink)]"
        aria-label="Toggle password visibility"
        tabIndex={-1}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  </div>
);

export default function ChangePassword() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const rules = {
    length: next.length >= MIN_LEN,
    match: next.length > 0 && next === confirm,
    different: next.length > 0 && next !== current,
  };
  const canSubmit = current && rules.length && rules.match && rules.different && !loading;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!current || !next || !confirm) return setError("Please complete all fields.");
    if (!rules.length) return setError(`Your new password must be at least ${MIN_LEN} characters.`);
    if (!rules.different) return setError("Your new password must be different from your current password.");
    if (!rules.match) return setError("The new passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
        confirm_password: confirm,
      });
      toast.success("Your password has been updated");
      setCurrent(""); setNext(""); setConfirm("");
      navigate("/dashboard");
    } catch (err) {
      setError(extractError(err, "Could not update your password."));
    } finally {
      setLoading(false);
    }
  };

  const Rule = ({ ok, children }) => (
    <li className={`flex items-center gap-2 ${ok ? "text-emerald-700" : "text-muted-foreground"}`}>
      <CheckCircle2 className={`h-3.5 w-3.5 ${ok ? "text-emerald-600" : "text-[color:var(--hh-line)]"}`} />
      {children}
    </li>
  );

  return (
    <div data-testid="change-password-page">
      <PageHeader title="Account" subtitle="Manage your sign-in details" />

      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="rounded-xl border bg-white p-6 shadow-[var(--shadow-sm)] lg:col-span-2">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--hh-amber-50)] text-[color:var(--hh-amber-600)]"><KeyRound className="h-5 w-5" /></span>
            <div>
              <h3 className="font-display text-lg font-semibold text-[color:var(--hh-ink)]">Change password</h3>
              <p className="text-sm text-muted-foreground">Choose a strong password only you know.</p>
            </div>
          </div>

          {user?.email && (
            <div className="mb-5 rounded-lg border border-[color:var(--hh-line)] bg-[var(--hh-band)] px-3 py-2 text-sm">
              <span className="text-muted-foreground">Signed in as </span>
              <span className="font-medium text-[color:var(--hh-ink)]">{user.email}</span>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-5" data-testid="change-password-form">
            <PasswordField id="current" label="Current password" value={current} onChange={setCurrent} show={showCurrent} setShow={setShowCurrent} testId="current-password-input" autoComplete="current-password" />
            <PasswordField id="next" label="New password" value={next} onChange={setNext} show={showNext} setShow={setShowNext} testId="new-password-input" autoComplete="new-password" />
            <PasswordField id="confirm" label="Confirm new password" value={confirm} onChange={setConfirm} show={showConfirm} setShow={setShowConfirm} testId="confirm-password-input" autoComplete="new-password" />

            <ul className="space-y-1.5 text-xs">
              <Rule ok={rules.length}>At least {MIN_LEN} characters</Rule>
              <Rule ok={rules.match}>New passwords match</Rule>
              <Rule ok={rules.different}>Different from your current password</Rule>
            </ul>

            {error && (
              <div data-testid="change-password-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {error}
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button type="submit" disabled={!canSubmit} data-testid="change-password-submit" className="h-11 rounded-xl bg-[hsl(var(--primary))] font-semibold text-white hover:bg-[var(--hh-amber-600)]">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating</> : "Update password"}
              </Button>
              <Button type="button" variant="ghost" className="h-11 rounded-xl" onClick={() => navigate("/dashboard")} data-testid="change-password-cancel">Cancel</Button>
            </div>
          </form>
        </Card>

        <Card className="h-fit rounded-xl border bg-white p-6 shadow-[var(--shadow-sm)]">
          <div className="flex items-center gap-2 text-[color:var(--hh-ink)]">
            <ShieldCheck className="h-5 w-5 text-[color:var(--hh-amber-600)]" />
            <h3 className="font-display text-base font-semibold">Keeping your account safe</h3>
          </div>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            <li>Use a password you don't use anywhere else.</li>
            <li>Mix letters, numbers and a symbol.</li>
            <li>Never share your password by email or phone.</li>
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">Signed in with a temporary password? Please change it here now.</p>
        </Card>
      </div>
    </div>
  );
}
